import { z } from 'zod';
import { CreateUserInputSchema } from '../users.js';

/**
 * @aiPurpose binding projection of a `call_center_agent`'s
 *   `CallCenterAgentProfile` joined to its `LeadSource` (#1261). Carries
 *   ONLY the safe source fields the admin surface needs to render the
 *   binding and the disabled-source warning.
 * @aiSecurity the source HMAC `secret` is NEVER part of this shape — it
 *   mirrors `LeadSourceSchema`'s omission so the admin account list can
 *   never leak secret material. `null` on the parent account means the
 *   agent has no binding row at all.
 */
export const CallCenterAccountBindingSchema = z
  .object({
    sourceId: z.string().uuid(),
    sourceCode: z.string().min(1),
    sourceDisplayName: z.string().min(1),
    sourceEnabled: z.boolean(),
  })
  .strict();
export type CallCenterAccountBinding = z.infer<typeof CallCenterAccountBindingSchema>;

/**
 * @aiPurpose read/list item for an external call-center account (#1261).
 *   One row per `call_center_agent` user with its current source binding
 *   (or `null`) and a derived `bindingWarning`.
 * @aiInvariant `bindingWarning` is `true` when the agent has NO binding
 *   OR its bound source is disabled/missing — the admin must rebind
 *   before the agent can submit (#1258 rejects on those states). The
 *   server derives it; clients must not recompute a divergent rule.
 * @aiSecurity never carries the source `secret`, a password, or any
 *   internal permission — the external role is submit-only.
 * @aiPii surfaces email + name — redact in logs.
 */
export const CallCenterAccountSchema = z
  .object({
    userId: z.string().uuid(),
    email: z.string().email(),
    name: z.string().min(1),
    active: z.boolean(),
    binding: CallCenterAccountBindingSchema.nullable(),
    bindingWarning: z.boolean(),
  })
  .strict();
export type CallCenterAccount = z.infer<typeof CallCenterAccountSchema>;

/**
 * @aiPurpose request body of `POST /call-center-agents` (#1261). The safe
 *   user-create fields plus the required source binding.
 * @aiInvariant `roleId` is INTENTIONALLY omitted — the server forces the
 *   `call_center_agent` system role so an admin cannot provision an
 *   external account into an internal role. `sourceId` is required and
 *   must reference an enabled source (the service rejects disabled /
 *   missing sources with 400).
 * @aiSecurity reuses `CreateUserInputSchema` fields verbatim so the
 *   email/password/phone validation contract stays in lockstep with the
 *   ordinary user-create path.
 * @aiPii contains email, name, phone, password — redact in logs.
 */
export const CreateCallCenterAccountInputSchema = CreateUserInputSchema.omit({ roleId: true })
  .extend({
    sourceId: z.string().uuid(),
  })
  .strict();
export type CreateCallCenterAccountInput = z.infer<typeof CreateCallCenterAccountInputSchema>;

/**
 * @aiPurpose request body of `PATCH /call-center-agents/:userId/binding`
 *   (#1261) — rebind an existing external account to a different source.
 * @aiInvariant rebinding is an UPDATE of the single `CallCenterAgentProfile`
 *   row (no history); the new source must exist and be enabled.
 */
export const UpdateCallCenterAccountBindingInputSchema = z
  .object({
    sourceId: z.string().uuid(),
  })
  .strict();
export type UpdateCallCenterAccountBindingInput = z.infer<
  typeof UpdateCallCenterAccountBindingInputSchema
>;

/**
 * @aiPurpose response of `GET /call-center-agents` (#1261).
 */
export const ListCallCenterAccountsResponseSchema = z
  .object({
    items: CallCenterAccountSchema.array(),
  })
  .strict();
export type ListCallCenterAccountsResponse = z.infer<typeof ListCallCenterAccountsResponseSchema>;

/**
 * @aiPurpose list item for the internal lead-intake duty admin surface
 *   (#1261). One row per eligible Manager (active + system `manager`
 *   role — the SAME predicate #1257's `pickNextManager` uses) with the
 *   current `onDuty` state (`false` when no `LeadIntakeManagerDuty` row
 *   exists yet).
 * @aiPii surfaces email + name — redact in logs.
 */
export const ManagerDutyItemSchema = z
  .object({
    userId: z.string().uuid(),
    name: z.string().min(1),
    email: z.string().email(),
    onDuty: z.boolean(),
  })
  .strict();
export type ManagerDutyItem = z.infer<typeof ManagerDutyItemSchema>;

/**
 * @aiPurpose response of `GET /lead-intake-duty` (#1261). `anyOnDuty`
 *   drives the empty-duty warning: when no eligible manager is on duty,
 *   freshly created intake leads have nobody to route to (#1257 returns
 *   an unrouted state).
 */
export const ListManagerDutyResponseSchema = z
  .object({
    items: ManagerDutyItemSchema.array(),
    anyOnDuty: z.boolean(),
  })
  .strict();
export type ListManagerDutyResponse = z.infer<typeof ListManagerDutyResponseSchema>;

/**
 * @aiPurpose request body of `PATCH /lead-intake-duty/:userId` (#1261) —
 *   toggle a manager's intake duty state.
 * @aiInvariant writes ONLY `LeadIntakeManagerDuty.onDuty`; it never
 *   touches `User.isOnShift` (logistics shift state is a separate
 *   column owned by `PATCH /users/me/shift`).
 */
export const ToggleManagerDutyInputSchema = z
  .object({
    onDuty: z.boolean(),
  })
  .strict();
export type ToggleManagerDutyInput = z.infer<typeof ToggleManagerDutyInputSchema>;

/**
 * @aiPurpose response of `PATCH /lead-intake-duty/:userId` (#1261).
 *   Echoes the post-toggle item so the client refreshes without a
 *   round-trip.
 */
export const ToggleManagerDutyResponseSchema = ManagerDutyItemSchema;
export type ToggleManagerDutyResponse = z.infer<typeof ToggleManagerDutyResponseSchema>;
